import type { PropsWithChildren, Ref } from 'react';
import { Box } from '@mui/material';
import { BACKGROUND_COLORS } from '@/common/presentation/constants/colors';
import { MetricCompatibleFontFaces } from '@/document-render/presentation/MetricCompatibleFontFaces';

interface Props extends PropsWithChildren {
  mode?: 'editor' | 'export';
  /** Attached to the measure wrapper so ResizeObserver can read host width
   * without being inflated by the unscaled A4 page min-content size. */
  scaleContainerRef?: Ref<HTMLDivElement>;
}

/**
 * On-screen preview canvas for DocumentRender (editor + LiveDocumentPreview/export).
 *
 * Pages are separate paper sheets (PageRender) — do not wrap them in one Paper
 * or multi-page previews look continuous and sheets sit flush to the card edge.
 *
 * Outer box is the ResizeObserver target: its width follows the host flex
 * allocation and must NOT depend on the unscaled A4 child min-content width
 * (that feedback loop caused continuous preview shaking / wrong pagination).
 */
export function DocumentFrame({ children, mode = 'editor', scaleContainerRef }: Props) {
  return (
    <Box
      ref={scaleContainerRef}
      data-testid="document-render:frame:root"
      data-mode={mode}
      sx={{
        // Overridable so a canvas that paints its own full-area backdrop (and zooms this frame with a
        // transform) can make the frame transparent instead of scaling the grey with the page.
        bgcolor: `var(--document-frame-bg, ${BACKGROUND_COLORS.GREY_200})`,
        borderRadius: 1,
        // Outer top/bottom (and side) spacing so sheets are detached from the
        // canvas edges — viewport chrome only; not printable A4 page padding.
        p: 3,
        overflowX: 'hidden',
        overflowY: 'visible',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 0
      }}
    >
      <MetricCompatibleFontFaces />
      {children}
    </Box>
  );
}
